import { queryOptions, useQuery } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { Schemas } from "../lib/api-schemas.ts";

import { CHAT_TEXTS as TEXTS } from "./texts.ts";

/**
 * Carga de datasets por id (seccion 9, regla de oro).
 *
 * Los componentes NO reciben los numeros por props: reciben un `datasetId` y
 * los piden aqui. Asi lo que se dibuja viene siempre del cálculo del backend,
 * y el modelo no tiene forma de transcribir una cifra equivocada.
 */
export type DatasetColumn = Readonly<Schemas["DatasetColumn"]>;
export type DatasetRow = Schemas["Dataset"]["rows"][number];
export type DatasetPayload = Readonly<Schemas["Dataset"]>;

/**
 * La consulta de un dataset.
 *
 * `queryOptions` empareja la clave con la función que trae los datos, así que
 * no hay forma de pedir una cosa con la clave de otra. Antes esto era un `Map`
 * a mano: la caché, el estado de carga y el error los pone ahora Query.
 */
export const datasetQuery = (datasetId: string) =>
  queryOptions({
    queryKey: ["dataset", datasetId] as const,
    queryFn: () => request<DatasetPayload>(`${API_BASE}/datasets/${datasetId}`),
    /**
     * Un dataset es INMUTABLE: se produce una vez y caduca a los siete días.
     * No hay nada que refrescar, así que no se vuelve a pedir en toda la
     * sesión aunque se dibuje en una tabla y en un gráfico.
     */
    staleTime: Infinity,
  });

export const useDataset = (datasetId: string) =>
  useQuery(datasetQuery(datasetId));

/** Formato de celda segun el tipo declarado por el backend. */
export const formatCell = (
  value: string | number | boolean | null,
  type: DatasetColumn["type"],
): string => {
  if (value === null) return "—";
  if (type === "boolean")
    return value === true ? TEXTS.value.yes : TEXTS.value.no;
  if (type === "number" && typeof value === "number") {
    return value.toLocaleString("es", { maximumFractionDigits: 2 });
  }
  return String(value);
};
